import { BRAND_NAME, BRAND_TAGLINE } from "@/lib/brand";
import type { OrganizationProfile } from "@/lib/site/organization-profile";
import { absoluteUrl } from "@/lib/seo/site";

/** Юрлицо оператора сервиса Idealitika (Smart Бизнес). */
export const COMPANY_LEGAL_NAME =
  'ООО «Смарт Бизнес Автоматизация»';

export const COMPANY_BRAND_NAME = "Smart Бизнес";

export const COMPANY_SITE_URL = "https://smartbiznes.org/";

export const COMPANY_EMAIL = "manager@smartbiznes.org";

export const COMPANY_PHONE_E164 = "+79581009458";

export const COMPANY_PHONE_DISPLAY = "+7 (958) 100-94-58";

export const COMPANY_ADDRESS = {
  country: "RU",
  locality: "Пенза",
  streetAddress: "ул. Московская, 74, 3 этаж, офис 303",
  /** Полная строка для UI (NAP). */
  formatted: "Россия, г. Пенза, ул. Московская, 74, 3 этаж, офис 303",
} as const;

/** Координаты офиса Smart Бизнес на [Яндекс.Картах](https://yandex.ru/maps/org/smart_biznes/189971336767/). */
export const COMPANY_GEO = {
  lat: 53.194497,
  lon: 45.015702,
} as const;

/** Карточка организации в Яндекс.Картах. */
export const COMPANY_YANDEX_MAP_URL =
  "https://yandex.ru/maps/org/smart_biznes/189971336767/?indoorLevel=1&ll=45.015702%2C53.194497&z=17";

/** Реквизиты ООО (документ в Bitrix24, как на [smartbiznes.org/documenti](https://smartbiznes.org/documenti)). */
export const COMPANY_REQUISITES_URL =
  "https://smartbiznes58.bitrix24.ru/doc/v7EpVM4oOfL6F4q0cfgD?externalLinkContext=eyJsaW5rSWQiOjU2Niwib2JqZWN0SWQiOjcwNTcyLCJ1bmlxdWVDb2RlIjoidjdFcFZNNG9PZkw2RjRxMGNmZ0QiLCJhdHRhY2hlZElkIjowLCJ2ZXJzaW9uSWQiOjB9.25a9a6d3575215f22b7f5ad3fcf52de2cde6e47839f2ce5e25efed0f9d28dbbd";

/** Профили компании для sameAs (дополняйте по мере появления). */
export const COMPANY_SAME_AS: string[] = [
  COMPANY_SITE_URL,
  COMPANY_YANDEX_MAP_URL,
  "https://www.tadviser.ru/index.php/Company:Smart_Biznes_Avtomatizatsiya",
];

export type OrganizationJsonLdNode = {
  "@type": "Organization";
  "@id": string;
  name: string;
  alternateName?: string;
  legalName?: string;
  url: string;
  logo: string;
  description: string;
  email?: string;
  telephone?: string;
  address?: {
    "@type": "PostalAddress";
    addressCountry: string;
    addressLocality: string;
    streetAddress: string;
  };
  areaServed?: string | { "@type": "Country"; name: string };
  sameAs?: string[];
};

/** Organization для JSON-LD (@graph, publisher, ContactPage). */
export function buildOrganizationJsonLdNode(
  options?: { siteUrl?: string; profile?: OrganizationProfile },
): OrganizationJsonLdNode {
  const profile = options?.profile;
  const siteUrl = options?.siteUrl ?? absoluteUrl("/");
  const orgId = `${siteUrl}/#organization`;

  return {
    "@type": "Organization",
    "@id": orgId,
    name: BRAND_NAME,
    alternateName: BRAND_TAGLINE,
    legalName: profile?.legalName ?? COMPANY_LEGAL_NAME,
    url: siteUrl,
    logo: absoluteUrl("/icon.png"),
    description:
      "AI research-engine для валидации бизнес-идей и анализа рынка РФ: отчёт 11+ страниц, scoring по 6 критериям, Wordstat и источники.",
    email: profile?.email ?? COMPANY_EMAIL,
    telephone: profile?.phoneE164 ?? COMPANY_PHONE_E164,
    address: {
      "@type": "PostalAddress",
      addressCountry: profile?.addressCountry ?? COMPANY_ADDRESS.country,
      addressLocality: profile?.addressLocality ?? COMPANY_ADDRESS.locality,
      streetAddress: profile?.addressStreet ?? COMPANY_ADDRESS.streetAddress,
    },
    areaServed: {
      "@type": "Country",
      name: "RU",
    },
    sameAs: profile?.sameAs?.length ? profile.sameAs : COMPANY_SAME_AS,
  };
}
